import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProjectClientView } from '@services/projects.service';

export const PROJECT_CLIENT_VIEW_QUERY_KEY = (projectId) => ['projects', 'client', projectId];

export const CLIENT_VIEW = {
    ALL: 'all',
    INHOUSE: 'inhouse',
};

function safeNumber(n, fallback = 0) {
    const v = Number(n);
    return Number.isFinite(v) ? v : fallback;
}

function sumPaymentAmounts(payments) {
    if (!Array.isArray(payments)) return 0;
    return payments.reduce((acc, p) => acc + safeNumber(p?.amount ?? p?.total ?? p?.paidAmount ?? 0, 0), 0);
}

export function useProjectClient(projectId) {
    const [view, setView] = useState(CLIENT_VIEW.ALL);

    const query = useQuery({
        queryKey: PROJECT_CLIENT_VIEW_QUERY_KEY(projectId),
        queryFn: () => getProjectClientView(projectId),
        enabled: !!projectId,
    });

    const data = query.data ?? null;
    const client = data?.client ?? null;
    const milestones = Array.isArray(data?.milestones) ? data.milestones : [];
    const changeRequests = Array.isArray(data?.changeRequests) ? data.changeRequests : [];
    const payments = Array.isArray(data?.payments) ? data.payments : [];
    const completionPercent = safeNumber(data?.completionPercent ?? 0, 0);

    const isInhouseClient = useMemo(() => {
        if (!client) return false;
        return client?.id == null;
    }, [client]);

    const visibleClient = useMemo(() => {
        if (view === CLIENT_VIEW.INHOUSE && !isInhouseClient) return null;
        return client;
    }, [client, isInhouseClient, view]);

    const kpis = useMemo(() => {
        const paymentsTotal = sumPaymentAmounts(payments);
        return {
            completionPercent,
            milestonesCount: milestones.length,
            changeRequestsCount: changeRequests.length,
            paymentsCount: payments.length,
            paymentsTotal,
        };
    }, [completionPercent, milestones.length, changeRequests.length, payments.length, payments]);

    const milestoneRows = useMemo(() => {
        return milestones.map((m) => {
            const sprints = Array.isArray(m?.sprints) ? m.sprints : [];
            const sprintCount = sprints.length;
            const taskCount = sprints.reduce((acc, s) => acc + (Array.isArray(s?.tasks) ? s.tasks.length : 0), 0);
            return {
                key: m?.id ?? `${m?.name ?? 'milestone'}`,
                name: m?.name ?? '-',
                status: m?.status ?? '-',
                sprintCount,
                taskCount,
            };
        });
    }, [milestones]);

    const milestoneColumns = useMemo(
        () => [
            { title: 'Milestone', dataIndex: 'name' },
            { title: 'Status', dataIndex: 'status' },
            { title: 'Sprints', dataIndex: 'sprintCount' },
            { title: 'Tasks', dataIndex: 'taskCount' },
        ],
        [],
    );

    const changeRequestRows = useMemo(() => {
        return changeRequests.map((cr) => ({
            key: cr?.id ?? `${cr?.title ?? cr?.name ?? cr?.createdAt ?? Math.random()}`,
            title: cr?.title ?? cr?.name ?? '-',
            status: cr?.status ?? '-',
            priority: cr?.priority ?? '-',
            createdAt: cr?.createdAt ?? cr?.created_on ?? '-',
        }));
    }, [changeRequests]);

    const changeRequestColumns = useMemo(
        () => [
            { title: 'Change Request', dataIndex: 'title' },
            { title: 'Status', dataIndex: 'status' },
            { title: 'Priority', dataIndex: 'priority' },
            { title: 'Created', dataIndex: 'createdAt' },
        ],
        [],
    );

    const paymentRows = useMemo(() => {
        return payments.map((p) => ({
            key: p?.id ?? `${p?.milestoneName ?? p?.createdAt ?? Math.random()}`,
            milestoneName: p?.milestoneName ?? '-',
            status: p?.status ?? '-',
            amount: safeNumber(p?.amount ?? p?.total ?? p?.paidAmount ?? 0, 0),
            createdAt: p?.createdAt ?? '-',
        }));
    }, [payments]);

    const paymentColumns = useMemo(
        () => [
            { title: 'Milestone', dataIndex: 'milestoneName' },
            { title: 'Status', dataIndex: 'status' },
            { title: 'Amount', dataIndex: 'amount' },
            { title: 'Created', dataIndex: 'createdAt' },
        ],
        [],
    );

    return {
        view,
        setView,
        client: visibleClient,
        rawClient: client,
        isInhouseClient,
        milestones,
        changeRequests,
        payments,
        completionPercent,
        kpis,
        milestoneColumns,
        milestoneRows,
        changeRequestColumns,
        changeRequestRows,
        paymentColumns,
        paymentRows,
        isLoading: query.isPending,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}

